/**
 * Screen mode type for Data Mode toggle configuration.
 * 
 * - 'demo': Toggle disabled, shows "Demo" only, always uses mock data
 * - 'live': Toggle disabled, shows "Live" only, always calls API
 * - 'all': Toggle enabled, user can switch between Demo/Live
 */
export type ScreenMode = 'demo' | 'live' | 'all';

/**
 * Configuration for Data Mode toggle availability per screen.
 * 
 * Key format: For top-level screens use the screen name (e.g., "Customers").
 * For sub-menu screens, concatenate parent and child with underscore (e.g., "Settings_Company").
 */
export const screenModeConfig: Record<string, ScreenMode> = {
  // Top-level screens
  Homepage: 'demo',
  Customers: 'all',
  Transactions: 'all',
  Payments: 'demo',
  Payments_Receipts: 'all',
  Payments_Allocate: 'all',
  Credit_Scoring: 'demo',

  // Collections sub-screens
  Collections_Dashboard: 'demo',
  Collections_Collections_Queue: 'demo',
  Collections_Promise_to_Pay: 'demo',

  // Analytics & Reporting sub-screens
  Analytics_Reporting_Ageing_Analysis: 'demo',

  // Dispute Management sub-screens
  Dispute_Management_Dashboard: 'demo',
  Dispute_Management_All_Disputes: 'all',

  // Settings sub-screens - Company and Users always use API (live mode)
  Settings_Company: 'live',
  Settings_Users: 'live',
  Settings_Integrations: 'demo',
  Settings_Templates: 'demo',
  Settings_Collection_Strategies: 'all',
  Settings_Dispute_Types: 'demo',
  Settings_AI_Configuration: 'demo',
};

/**
 * Get the screen key from a pathname.
 * Maps URL paths to the screen configuration keys.
 */
export function getScreenKeyFromPath(pathname: string): string | null {
  const pathToKeyMap: Record<string, string> = {
    '/': 'Homepage',
    '/customers': 'Customers',
    '/transactions': 'Transactions',
    '/payments': 'Payments',
    '/payments/receipts': 'Payments_Receipts',
    '/payments/allocate': 'Payments_Allocate',
    '/credit-scoring': 'Credit_Scoring',

    // Collections
    '/collections/dashboard': 'Collections_Dashboard',
    '/collections/queue': 'Collections_Collections_Queue',
    '/collections/promises': 'Collections_Promise_to_Pay',

    // Analytics & Reporting
    '/analytics-reporting/ageing-analysis': 'Analytics_Reporting_Ageing_Analysis',

    // Dispute Management
    '/disputes': 'Dispute_Management_Dashboard',
    '/disputes/all': 'Dispute_Management_All_Disputes',

    // Settings
    '/settings/company': 'Settings_Company',
    '/users': 'Settings_Users',
    '/integrations': 'Settings_Integrations',
    '/settings/templates': 'Settings_Templates',
    '/settings/strategies': 'Settings_Collection_Strategies',
    '/settings/strategies/create': 'Settings_Collection_Strategies',
    '/settings/strategies/edit': 'Settings_Collection_Strategies',
    '/settings/dispute-types': 'Settings_Dispute_Types',
    '/settings/ai-configuration': 'Settings_AI_Configuration',
  };

  // First try exact match
  if (pathToKeyMap[pathname]) {
    return pathToKeyMap[pathname];
  }

  // For customer detail pages like /customers/123, return Customers
  if (pathname.startsWith('/customers/')) {
    return 'Customers';
  }

  // For receipts detail pages like /payments/receipts/123, return Payments_Receipts
  if (pathname.startsWith('/payments/receipts/')) {
    return 'Payments_Receipts';
  }

  // For allocation workspace pages like /payments/allocate/123, return Payments_Allocate
  if (pathname.startsWith('/payments/allocate/')) {
    return 'Payments_Allocate';
  }

  return null;
}

/**
 * Get the screen mode for a given pathname.
 * Returns 'demo' | 'live' | 'all' based on the screen configuration.
 */
export function getScreenModeForPath(pathname: string): ScreenMode {
  const screenKey = getScreenKeyFromPath(pathname);
  if (screenKey === null) {
    // Unknown screen, default to demo only
    return 'demo';
  }
  return screenModeConfig[screenKey] ?? 'demo';
}

/**
 * Check if the Data Mode toggle should be enabled for a given pathname.
 * Returns true if toggle should be shown (mode is 'all'), false otherwise.
 */
export function isToggleEnabledForPath(pathname: string): boolean {
  return getScreenModeForPath(pathname) === 'all';
}
